import { logger } from './logger.js';
import { escapeHtml } from './sanitize.js';
import { apiCall, endpoints } from '../checkout-core/api.js';

const BOT_PROTECTION_CACHE = {};
const BOT_PROTECTION_SCRIPTS = {};
const BOT_PROTECTION_SURFACE = 'ticket_purchase';

const getCacheKey = (eventName, inviteToken, inviteCode) =>
  [eventName || '', inviteToken || '', inviteCode || ''].join('|');

function loadBotScript(src, id) {
  if (id && document.getElementById(id)) return Promise.resolve();
  if (BOT_PROTECTION_SCRIPTS[src]) return BOT_PROTECTION_SCRIPTS[src];

  BOT_PROTECTION_SCRIPTS[src] = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    if (id) script.id = id;
    script.src = src;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('script_load_failed'));
    document.head.appendChild(script);
  });

  return BOT_PROTECTION_SCRIPTS[src];
}

export async function getBotProtectionConfig(
  eventName,
  { inviteToken, inviteCode, force = false } = {},
) {
  if (!eventName) return { enabled: false, mode: 'none' };
  const cacheKey = getCacheKey(eventName, inviteToken, inviteCode);
  if (!force && BOT_PROTECTION_CACHE[cacheKey]) return BOT_PROTECTION_CACHE[cacheKey];

  try {
    const res = await apiCall(endpoints.getBotProtectionConfig, {
      event: eventName,
      invite_token: inviteToken || undefined,
      invite_code: inviteCode || undefined,
      surface: BOT_PROTECTION_SURFACE,
    }, { method: 'GET' });
    const cfg = (res.success ? res.data : null) || { enabled: false, mode: 'none' };
    BOT_PROTECTION_CACHE[cacheKey] = cfg;
    return cfg;
  } catch (error) {
    logger.warn('Bot protection config failed', error);
    return { enabled: false, mode: 'none' };
  }
}

export async function injectBotProtection(form, cfg, { force = false } = {}) {
  if (!form || !cfg?.enabled || cfg.mode === 'none') return;
  if (!force && form.dataset.botProtectionInit === '1') return;
  const existing = form.querySelector('[data-bot-protection="1"]');
  if (existing) existing.remove();
  form.dataset.botProtectionInit = '1';

  const submitBtn = form.querySelector('button[type="submit"]') || form.querySelector('.submit-btn');
  const container = document.createElement('div');
  container.className = 'mt-4 p-3 rounded-lg border border-default-theme surface-card text-sm';
  container.dataset.botProtection = '1';

  if (cfg.error_code) {
    container.innerHTML = `
      <label class="checkout-label">${__('Bot-Schutz')} *</label>
      <p class="text-xs checkout-promo-error mb-2">${escapeHtml(cfg.message || __('Bot-Schutz ist fehlerhaft konfiguriert.'))}</p>
    `;
  } else if (cfg.mode === 'simple') {
    container.innerHTML = `
      <label class="checkout-label">${__('Sicherheitsfrage')} *</label>
      <p class="text-xs checkout-helper-text mb-2">${escapeHtml(cfg.challenge?.question || __('Bitte beantworte die Sicherheitsfrage.'))}</p>
      <input type="text" name="bot_challenge_response" class="checkout-input" placeholder="${__('Antwort eingeben')}" autocomplete="off" required>
    `;
  } else {
    container.innerHTML = `
      <label class="checkout-label">${__('Bot-Schutz')} *</label>
      <div class="bot-protection-widget"></div>
      <input type="hidden" name="bot_challenge_response" />
    `;
    const widget = container.querySelector('.bot-protection-widget');
    const input = container.querySelector('input[name="bot_challenge_response"]');

    if (!cfg.site_key) {
      widget.textContent = __('Captcha ist nicht konfiguriert.');
    } else if (cfg.mode === 'turnstile') {
      await loadBotScript('https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit', 'turnstile-api');
      if (window.turnstile) {
        window.turnstile.render(widget, {
          sitekey: cfg.site_key,
          callback: (token) => { input.value = token || ''; },
          'expired-callback': () => { input.value = ''; },
        });
      }
    } else {
      widget.textContent = __('Dieser Bot-Schutz wird auf der Ticketkauf-Seite nicht unterstützt.');
    }
  }

  if (submitBtn && submitBtn.parentNode) {
    submitBtn.parentNode.insertBefore(container, submitBtn);
  } else {
    form.appendChild(container);
  }
}

export async function resolveBotProtectionResponse(cfg, form) {
  if (!cfg?.enabled || cfg.mode === 'none') return null;
  if (cfg.error_code) return null;

  if (cfg.mode === 'simple') {
    const input = form?.querySelector('input[name="bot_challenge_response"]');
    const answer = input?.value?.trim();
    if (!answer) return null;
    // S11-003 Roundtrip-Vollfix: Der Backend-Write (generate_simple_challenge)
    // keyt auf einer per-Challenge-UUID (cfg.challenge.challenge_id). Diese MUSS
    // zurueck an den Validator, sonst trifft der Read den falschen Redis-Key
    // (Fallback session.sid != UUID) und jede simple-Challenge schlaegt fehl.
    // Backend (_validate_simple_challenge) parst den {answer, challenge_id}-
    // Envelope. Ohne challenge_id (alt-gecachtes cfg) faellt es auf die rohe
    // Antwort zurueck → Backend fail-closed (deny), nie ein falscher Pass.
    const challengeId = cfg.challenge?.challenge_id;
    return challengeId
      ? JSON.stringify({ answer, challenge_id: challengeId })
      : answer;
  }

  const input = form?.querySelector('input[name="bot_challenge_response"]');
  return input?.value || null;
}

export async function refreshBotProtection(form, eventName, opts = {}) {
  const cfg = await getBotProtectionConfig(eventName, { ...opts, force: true });
  await injectBotProtection(form, cfg, { force: true });
  return cfg;
}
